import { endpoints } from '@/api/endpoints';
import { useAuth } from '@/auth/AuthProvider';
import { MasterCrud } from '@/components/MasterCrud';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Money } from '@/components/ui/Money';
import { fmtDate } from '@/utils/format';

interface ExpenseRow {
  id: number;
  description: string;
  amount: number;
  category?: string;
  payment_mode?: string;
  reference_number?: string;
  paid_at?: string;
}

interface ExpenseForm {
  description: string;
  amount: string;
  category: string;
  payment_mode: 'cash' | 'upi' | 'card' | 'bank_transfer';
  reference_number: string;
  paid_at: string;
}

const CATEGORIES = [
  'rent',
  'salary',
  'utilities',
  'transport',
  'supplies',
  'marketing',
  'other',
];

export function ExpensesListPage() {
  const { hasRole } = useAuth();
  // CA / accountant gets read-only — they reconcile, they don't enter.
  const readOnly = !hasRole('owner') && !hasRole('manager');

  return (
    <MasterCrud<ExpenseRow, ExpenseForm>
      title="Expenses"
      description="Rent, salaries, utilities — every paisa that leaves the till."
      listUrl={endpoints.expenses}
      itemUrl={endpoints.expense}
      queryKey="expenses"
      readOnly={readOnly}
      newForm={() => ({
        description: '',
        amount: '',
        category: 'other',
        payment_mode: 'cash',
        reference_number: '',
        paid_at: new Date().toISOString().slice(0, 10),
      })}
      toForm={(r) => ({
        description: r.description ?? '',
        amount: r.amount?.toString() ?? '',
        category: r.category ?? 'other',
        payment_mode: (r.payment_mode as ExpenseForm['payment_mode']) ?? 'cash',
        reference_number: r.reference_number ?? '',
        paid_at: r.paid_at?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
      })}
      toBody={(f) => ({
        description: f.description.trim(),
        amount: Number(f.amount),
        category: f.category,
        payment_mode: f.payment_mode,
        reference_number: f.reference_number.trim() || undefined,
        paid_at: f.paid_at,
      })}
      columns={[
        {
          key: 'description',
          header: 'Description',
          cell: (r) => (
            <div>
              <div className="font-medium">{r.description}</div>
              <div className="text-xs text-ink-soft capitalize">
                {r.category ?? 'other'} · {r.payment_mode ?? '—'}
              </div>
            </div>
          ),
        },
        {
          key: 'when',
          header: 'Date',
          hideOnMobile: true,
          cell: (r) => fmtDate(r.paid_at),
        },
        {
          key: 'amount',
          header: 'Amount',
          className: 'text-right',
          cell: (r) => (
            <Money value={r.amount} className="font-semibold" tone="negative" />
          ),
        },
      ]}
      formFields={(value, setValue) => (
        <div className="space-y-3">
          <Input
            name="description"
            label="Description"
            autoFocus
            value={value.description}
            onChange={(e) => setValue({ ...value, description: e.target.value })}
            placeholder="Shop rent, December"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              name="amount"
              label="Amount (₹)"
              type="number"
              inputMode="decimal"
              value={value.amount}
              onChange={(e) => setValue({ ...value, amount: e.target.value })}
            />
            <Input
              name="paid_at"
              label="Date"
              type="date"
              value={value.paid_at}
              onChange={(e) => setValue({ ...value, paid_at: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select
              name="category"
              label="Category"
              value={value.category}
              onChange={(e) => setValue({ ...value, category: e.target.value })}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c} className="capitalize">
                  {c}
                </option>
              ))}
            </Select>
            <Select
              name="payment_mode"
              label="Paid via"
              value={value.payment_mode}
              onChange={(e) =>
                setValue({
                  ...value,
                  payment_mode: e.target.value as ExpenseForm['payment_mode'],
                })
              }
            >
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
              <option value="bank_transfer">Bank transfer</option>
            </Select>
          </div>
          <Textarea
            name="reference_number"
            rows={2}
            label="Reference number (optional)"
            value={value.reference_number}
            onChange={(e) =>
              setValue({ ...value, reference_number: e.target.value })
            }
          />
        </div>
      )}
    />
  );
}
